import React, { useState, useEffect } from 'react';
import styles from './ReportStudio.module.css';
import { FiDownload } from 'react-icons/fi';

const ReportStudio = () => {
    const [loading, setLoading] = useState(false);
    const [completedCount, setCompletedCount] = useState(0);
    const [draftsCount, setDraftsCount] = useState(0);
    const [message, setMessage] = useState({ text: '', type: '' });

    const getToken = () => {
        return localStorage.getItem('token');
    };

    const fetchCounts = async () => {
        try {
            const token = getToken();
            const response = await fetch('https://insightsdemo.onrender.com/api/studies/total-reports', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();
            if (data.success) {
                setCompletedCount(data.data.totalCompleted || 0);
                setDraftsCount(data.data.totalDrafts || 0);
            }
        } catch (error) {
            console.error('Error fetching counts:', error);
        }
    };

    const handleExport = async (type) => {
        setLoading(true);
        setMessage({ text: '', type: '' });

        try {
            const token = getToken();
                const response = await fetch('https://insightsdemo.onrender.com/api/studies/export', {
                    method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ type })
            });

            // Check if response is OK
            if (!response.ok) {
                const errorText = await response.text();
                let errorMessage = 'Export failed';
                try {
                    const errorJson = JSON.parse(errorText);
                    errorMessage = errorJson.message || errorMessage;
                } catch {
                    errorMessage = errorText || errorMessage;
                }
                throw new Error(errorMessage);
            }

            // Get the raw blob from response
            const blob = await response.blob();
            
            // Verify blob has content
            if (blob.size === 0) {
                throw new Error('Received empty file from server');
            }

            // Create a proper Excel blob with correct MIME type
            const excelBlob = new Blob([blob], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            
            // Create download link
            const url = window.URL.createObjectURL(excelBlob);
            const link = document.createElement('a');
            link.href = url;
            
            // Generate filename with current date
            const date = new Date().toISOString().split('T')[0];
            link.download = `${type}-submissions-${date}.xlsx`;
            
            // Trigger download
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, 100);

            setMessage({ text: 'Export successful! File is downloading...', type: 'success' });
            setTimeout(() => setMessage({ text: '', type: '' }), 3000);

        } catch (error) {
            console.error('Export error:', error);
            setMessage({ 
                text: `Export failed: ${error.message || 'Unknown error occurred'}`, 
                type: 'error' 
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCounts();
    }, []);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.titleSection}>
                    <h1 className={styles.title}>
                        <FiDownload className={styles.titleIcon} />
                        Export Data to Excel
                    </h1>
                    <p className={styles.subtitle}>
                        Download survey submissions to Excel format for further analysis
                    </p>
                </div>
            </div>

            {message.text && (
                <div className={`${styles.message} ${message.type === 'success' ? styles.messageSuccess : styles.messageError}`}>
                    {message.text}
                </div>
            )}

            <div className={styles.exportCards}>
                <div className={styles.card}>
                    <h3 className={styles.cardTitle}>Completed Only</h3>
                    <p className={styles.cardCount}>{completedCount} completed records</p>
                    <button
                        className={styles.exportButtonPrimary}
                        onClick={() => handleExport('completed')}
                        disabled={loading || completedCount === 0}
                    >
                        <FiDownload />
                        Export Completed
                    </button>
                </div>

                <div className={styles.card}>
                    <h3 className={styles.cardTitle}>Drafts Only</h3>
                    <p className={styles.cardCount}>{draftsCount} draft records</p>
                    <button
                        className={styles.exportButtonSecondary}
                        onClick={() => handleExport('draft')}
                        disabled={loading || draftsCount === 0}
                    >
                        <FiDownload />
                        Export Drafts
                    </button>
                </div>
            </div>

            <div className={styles.infoSection}>
                <h4 className={styles.infoTitle}>Export Information</h4>
                <ul className={styles.infoList}>
                    <li>Files are named with export date for easy tracking</li>
                    <li>Compatible with Microsoft Excel, Google Sheets, and other spreadsheet applications</li>
                </ul>
            </div>
        </div>
    );
};

export default ReportStudio;
